import React, { useState } from "react";
import Button from "../Button/Button";
import { ReactComponent as PdfIcon } from "../../assets/icons/pdf.svg";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate } from "../../utils/entityUtils";
import SuiviHeader from "../SuiviHeader/SuiviHeader";
import ReactDOMServer from "react-dom/server";

interface PrintButtonProps {
    targetRef: React.RefObject<HTMLElement>;
    fileName?: string;
    value?: string;
    className?: string;
    suiviHeaderProps: {
        titre: string;
        sousTitre: string;
        subtitle: string;
        status: string;
        statusColor: string;
        delais?: string;
        backgroundImage?: string;
    };
}

export const PrintButton: React.FC<PrintButtonProps> = ({
    targetRef,
    fileName = "document",
    value = "PDF",
    className = "",
    suiviHeaderProps
}) => {
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const syncFormValues = (source: HTMLElement, target: HTMLElement) => {
        const sync = (selector: string, prop: keyof HTMLInputElement) => {
            const src = source.querySelectorAll(selector);
            const tgt = target.querySelectorAll(selector);
            src.forEach((el, i) => ((tgt[i] as any)[prop] = (el as any)[prop]));
        };
        sync("select", "value");
        sync("textarea", "value");
        sync("input[type='text']", "value");
        sync("input[type='number']", "value");
        sync("input[type='checkbox']", "checked");
        sync("input[type='radio']", "checked");
    };

    const handlePrint = async () => {
        if (!targetRef.current || isLoading) return;
        setIsLoading(true);

        try {
            await new Promise<void>((resolve) => {
                const section = targetRef.current?.querySelector('.analyse-financiere-loaded');
                if (!section) {
                    // Si elle n'existe pas, on continue immédiatement
                    return resolve();
                }

                // Sinon, on attend qu’elle soit effectivement chargée
                const timeout = setTimeout(() => resolve(), 5000); // sécurité anti-blocage
                const check = () => {
                    if (targetRef.current?.querySelector('.analyse-financiere-loaded')) {
                        clearTimeout(timeout);
                        resolve();
                    } else {
                        setTimeout(check, 50);
                    }
                };
                check();
            });

            // 2️⃣ Attendre que toutes les images soient chargées
            const images = Array.from(targetRef.current.querySelectorAll("img"));
            await Promise.all(
                images.map((img) => {
                    if (img.complete) return Promise.resolve();
                    return new Promise((resolve) => {
                        img.onload = img.onerror = () => resolve(null);
                    });
                })
            );

            await new Promise((resolve) => setTimeout(resolve, 300)); // petit délai pour le rendu

            // 3️⃣ Cloner le contenu et synchroniser les formulaires
            const content = targetRef.current.cloneNode(true) as HTMLElement;
            syncFormValues(targetRef.current, content);


            // 4️⃣ Remplacer textareas par div
            const textareas = content.querySelectorAll("textarea");
            textareas.forEach((textarea) => {
                const div = document.createElement("div");
                div.innerText = textarea.value;
                Object.assign(div.style, {
                    whiteSpace: "pre-wrap",
                    border: "1px solid #ccc",
                    padding: "8px",
                    borderRadius: "6px",
                    backgroundColor: "#f9f9f9",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    color: "#333",
                });
                textarea.parentNode?.replaceChild(div, textarea);
            });

            // 5️⃣ Remplacer selects par div
            const selects = content.querySelectorAll("select");
            selects.forEach((select) => {
                const selectedOption = select.options[select.selectedIndex];
                const div = document.createElement("div");
                div.innerText = selectedOption ? selectedOption.textContent || "" : "";
                Object.assign(div.style, {
                    border: "1px solid #ccc",
                    padding: "8px",
                    borderRadius: "6px",
                    backgroundColor: "#f9f9f9",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    color: "#333",
                });
                select.parentNode?.replaceChild(div, select);
            });

            // 6️⃣ Textes overflow
            const textDivs = content.querySelectorAll(".text-other-user");
            textDivs.forEach((div) => {
                Object.assign(div.style, {
                    maxHeight: "none",
                    overflow: "visible",
                    background: "white",
                });
            });

            // 7️⃣ Styles globaux
            const styles = Array.from(
                document.querySelectorAll("link[rel='stylesheet'], style")
            ).map((node) => node.outerHTML).join("\n");

            // 8️⃣ Header suivi
            const suiviHeaderHTML = ReactDOMServer.renderToString(
                <SuiviHeader {...suiviHeaderProps} noNavigation={true} />
            );

            const now = new Date();
            const header = user?.email
                ? `<div style="font-size:1.2rem;font-weight:600;margin-bottom:20px;color:#333;">
                Imprimé par : ${user.email} le ${formatDate(now)}
              </div>`
                : "";

            // 9️⃣ Ouvrir la fenêtre et imprimer
            const printWindow = window.open("", "_blank");
            if (!printWindow) return;

            printWindow.document.open();
            printWindow.document.write(`
            <html lang="fr">
                <head>
                    <meta charset="UTF-8" />
                    <title>${fileName}</title>
                    ${styles}
                    <style>
                        @page { margin: 10mm 5mm; }
                        body {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                            background: white;
                            font-family: Arial, sans-serif;
                            margin: 0;
                            padding: 0px;
                        }
                        
                        @media print {
                            .text-other-user {
                                max-height: none !important;
                                overflow: visible !important;
                                background: white !important;
                            }
                            .page-break {
                                page-break-before: always !important;
                                break-before: always !important;
                            }
                            .avoid-break {
                                page-break-inside: avoid; !important
                                break-inside: avoid !important;
                            }
                            .no-print, .no-print * {
                                display: none !important;
                            }
                        }
                    </style>
                </head>
                <body>
                    ${header}
                    ${suiviHeaderHTML}
                    ${content.outerHTML}
                </body>
            </html>
        `);
            printWindow.document.close();

            printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
                setTimeout(() => printWindow.document.close(), 500);
            };

        } catch (error) {
            console.error("Erreur lors de la préparation du PDF :", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Button
            value={isLoading ? "Préparation..." : value}
            icon={<PdfIcon />}
            variant="secondary"
            className={`${className} no-print`}
            onClick={handlePrint}
            size="small"
            disabled={isLoading}
        />
    );
};